<template>
    <b-modal
            :visible="show"
            @hidden="hideEvents"
            title="Submit action for approval"
            :id="modalId"
            scrollable
    >
        <vue-element-loading :active="loading" :is-full-screen="false"/>
        <b-form>
            <b-form-textarea
                    class="mb-3"
                    placeholder="Write a note..."
                    rows="6"
                    v-model="$v.form.note.$model"
                    :state="getState($v.form.note)"
            >
            </b-form-textarea>

            <form-error v-if="$v.form.$error">
                Please enter a note.
            </form-error>

            <vue-dropzone
                    id="fileDrop"
                    ref="dropzone"
                    :options="dropOptions"
                    @vdropzone-sending-multiple="attachParams"
                    @vdropzone-success="success"
                    @vdropzone-queue-complete="onQueueComplete"
                    @vdropzone-error="error"
                    @vdropzone-file-added="checkForDuplicates"
            >
            </vue-dropzone>
        </b-form>
        <template slot="modal-footer">
            <b-button
                    variant="outline-secondary"
                    @click="hideModal">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    class="btn-arrow"
                    variant="yellow"
                    @click="submitForApproval"
                    :disabled="disabled"
            >
                Submit for approval
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import CaretIcon from '@/components/Form/CaretIcon';
import ModalMixin from '@/components/Modal/ModalMixin';
import { singleRequestUploadOptions } from '@/components/Form/singleRequestUpload';
import vueDropzone from 'vue2-dropzone';
import FormValidate from '@/services/Mixins/FormValidate';
import { required } from 'vuelidate/lib/validators';
import ActionStatus from "@/services/Action/ActionStatus";

export default {
  name: 'Submit-Approval-Modal',
  components: {
    CaretIcon,
    vueDropzone,
  },
  props: {
    clientActionId: {
      required: true,
    },
    type: {
      required: true,
    },
  },
  mixins: [ModalMixin, FormValidate],
  data() {
    return {
      modalId: 'submitForApprovalModal',
      loading: false,
      displayedNotification: false,
      displayedError: false,
      submitted: false,
      form: {
        note: '',
      },
      dropOptions: {
        url: () => this.url,
        dictRemoveFile: '❌',
        dictDefaultMessage: 'Drop files here to upload, or click to <span class="underline">select</span> a file to upload.',
        addRemoveLinks: true,
        autoProcessQueue: false,
        uploadMultiple: true,
        ...singleRequestUploadOptions,
        thumbnailWidth: 125,
        thumbnailHeight: 125,
        acceptedFiles: '.csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png,.eml,.ppt,.pptx,.pptm,.pps,.ppsx',
        headers: {
          Authorization: `Bearer ${this.$store.getters.authToken}`,
        },
        renameFile(file) {
          const fileParts = file.name.split('.');
          const extension = fileParts[fileParts.length -1].toLowerCase();
          fileParts[fileParts.length -1] = extension;
          return fileParts.join('.');
        },
      },
    };
  },
  validations: {
    form: {
      note: {
        required,
      },
    },
  },
  computed: {
    disabled() {
      return this.$v.form.$invalid || this.loading;
    },
    url() {
      return `${api.defaults.baseURL}/actions/${this.type}/${this.clientActionId}/approve/submit`;
    },
  },
  methods: {
    submitForApproval() {
      if (!this.validate()) {
        return;
      }

      this.displayedNotification = false;
      this.displayedError = false;
      this.submitted = true;
      this.loading = true;
      this.$refs.dropzone.processQueue();
    },

    success(file, response) {
      if (this.displayedNotification) {
        return;
      }

      const success = file.status === 'success';

      const title = 'Action submitted';
      let text = 'Action has been submitted for approval.';
      let type = 'success';

      if (!success) {
        type = 'error';
        text += 'Action failed to be submitted. Please try again later.';
      }

      this.displayedNotification = true;

      this.$notify({
        duration: 7000,
        title,
        text,
        type,
      });
      this.$store.dispatch('updateActionField', { field: 'status', value: ActionStatus.PENDING_APPROVAL() });
      this.$emit('submitted', true);
    },

    error(file, message) {
      // No xhr means Dropzone refused the file before sending it, and its
      // message says why.
      const rejected = !file.xhr;

      if (rejected) {
        this.$refs.dropzone.removeFile(file);
      } else {
        // Dropzone reports a failed request once per file for every file in it.
        if (this.displayedError) {
          return;
        }

        this.displayedError = true;
      }

      this.$notify({
        title: 'Error',
        text: rejected ? message : 'Something went wrong. Please try again later.',
        type: 'error',
      });
    },

    complete() {
      this.submitted = false;
      this.hideModal();
      this.loading = false;
    },

    // Dropzone also completes the queue when it refuses a file, and many times
    // over for one failed request. Only the first completion after a send closes.
    onQueueComplete() {
      if (!this.submitted) {
        return;
      }

      this.complete();
    },

    validate() {
      if (this.getFiles().length === 0) {
        this.$notify({
          title: 'Error',
          text: 'Please upload a file as an approval document.',
          type: 'error',
        });
        return false;
      }

      return true;
    },

    attachParams(files, xhr, formData) {
      formData.append('clientActionId', this.clientActionId);
      formData.append('note', this.form.note);
    },

    getFiles() {
      return this.$refs.dropzone.getQueuedFiles();
    },

    hideModalEvents() {
      this.form.note = '';
      this.$v.$reset();
    },

    checkForDuplicates(file) {
      const existingFiles = this.$refs.dropzone.getAcceptedFiles();
      const duplicate = existingFiles.find(f =>
        f !== file &&
          f.name === file.name &&
          f.size === file.size &&
          f.type === file.type);

      if (duplicate) {
        this.$refs.dropzone.removeFile(file);
        this.$notify({
          title: 'Duplicate file',
          text: `Duplicate images detected, duplicates will be removed.`,
          type: 'warn',
        });
      }
    },
  },
};
</script>

<style lang="scss">
    .dz-progress {
        display: none;
    }

    .vue-dropzone .dz-preview {
        .dz-remove {
            top: 0;
            right: 0;
            opacity: 1;
            background: rgba(78, 115, 138, 0.95);
            bottom: initial;
            padding: 5px 7.5px;
            border: 0;
        }
    }

    .underline {
        text-decoration: underline;
    }
</style>
